import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { getSubscriptionCheckout } from "../../../../../api/services/subscription.js";
import { Text, Button, Icon } from "@virkesborsen/ui-component-library";
import KlarnaCheckout from "../../DFRCheckoutDialog/components/KlarnaCheckout.js";
import { TailwindWrapper } from "../../../../TailwindWrapper/TailwindWrapper.jsx";
import { useSubscription } from "../../../context/SubscriptionCheckoutContext.js";

/**
 *  Klarna HTML is rendered outside TailwindWrapper because Klarna Checkout
 *  can't connect to its external API when inside a shadowRoot for some reason,
 *   */
export default function Payment({ isOpen, handleClose }) {
  const {
    previousStep,
    nextStep,
    selectedProperties,
    couponDiscount,
    extraData,
    setPaymentMethod,
  } = useSubscription();
  const [klarnaHtml, setKlarnaHtml] = useState(undefined);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentSolution = async () => {
      setLoading(true);
      const requestBody = {
        refs: selectedProperties.map((prop) => prop.ref),
        // subscription_id: selectedSubscription?.id,
        coupon_code: couponDiscount?.coupon_code,
        extra_data: {
          location_terms: extraData?.locationTerms,
          ownership_terms: extraData?.ownershipTerms,
        },
      };

      const response = await getSubscriptionCheckout(requestBody);

      if (response.ok) {
        if (response.data.klarna_html) {
          await setPaymentMethod("klarna");
          setKlarnaHtml(response.data.klarna_html);
        } else {
          await setPaymentMethod("invoice");
          nextStep();
        }
      } else {
        setError(response.message);
        console.error(
          "Payment solution fetch error: ",
          response.status,
          response.data
        );
      }
      setLoading(false);
    };

    fetchPaymentSolution();
  }, []);

  if (!isOpen) {
    return null;
  }

  if (error) {
    console.error("Payment solution fetch error: ", error);
  }

  return createPortal(
    <StyledBackdrop>
      <StyledModal>
        <StyledModalHeader>
          <TailwindWrapper>
            <div className="flex w-full justify-between gap-4 items-start">
              <div className="flex flex-row gap-4 items-center">
                <Button
                  tw
                  variant="tertiary"
                  size="icon"
                  onClick={previousStep}
                >
                  <Icon
                    tw
                    name="ArrowRightSimple"
                    size="large"
                    className="rotate-180"
                  />
                </Button>
                <div className="flex flex-col gap-1">
                  <Text variant="text-base/leading-normal/medium">
                    Beställ Treebula Plus
                  </Text>
                </div>
              </div>
              <Button tw variant="tertiary" size="icon" onClick={handleClose}>
                <Icon
                  tw
                  name="CrossSimple"
                  className="color-charcoal-500"
                  size="large"
                />
              </Button>
            </div>
          </TailwindWrapper>
        </StyledModalHeader>
        <StyledModalContentWrapper>
          <StyledModalContent>
            {klarnaHtml && <KlarnaCheckout htmlMarkup={klarnaHtml} />}
            {error && (
              <StyledText>
                Oops! Något gick fel. Ring 08-33 99 44 så hjälper vi dig vidare.
              </StyledText>
            )}
            {loading && <StyledText>Loading...</StyledText>}
          </StyledModalContent>
        </StyledModalContentWrapper>
      </StyledModal>
    </StyledBackdrop>,
    document.body
  );
}

const StyledBackdrop = styled.div`
  background-color: rgba(1, 7, 14, 0.67);
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1045;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 300ms ease-out;
`;

const StyledModal = styled.div`
  background-color: #f7f5f2;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  margin: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(22, 22, 34, 0.09);

  @media (min-width: 768px) {
    max-height: 780px;
    max-width: 720px;
    border-radius: 20px;
    box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.475);
  }
`;

const StyledModalHeader = styled.div`
  background-color: #fbfaf8;
  border-color: rgba(22, 22, 34, 0.09); /* charcoal-alpha-50 */
  border-width: 1px 0 1px 0;
  border-style: solid;
  display: flex;
  flex: none;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const StyledModalContentWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
`;

const StyledText = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const StyledModalContent = styled.div`
  display: flex;
  flex: 1;
`;
